import { makeAutoObservable } from "mobx";
import { RootStore } from "stores";

const TAB_NAMES = ["general", "structures", "lineitems", "schedule"];

export default class JobDetailViewStore {
  projectId: string | null = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as any);
  }

  setProjectId(id: string | null) {
    this.projectId = id;
  }

  get activeTab(): number {
    const tabName =
      (this.rootStore as any).routerStore?.queryParams?.tab || "general";
    const index = TAB_NAMES.indexOf(tabName);
    return index >= 0 ? index : 0;
  }

  setActiveTab(index: number) {
    const tabName = TAB_NAMES[index] || "general";
    (this.rootStore as any).routerStore.goTo(null, null, {
      ...(this.rootStore as any).routerStore.queryParams,
      tab: tabName,
    });
  }

  // --- Entity accessors ---

  get allProjects() {
    return (
      ((this.rootStore as any).manholeProjectsStore as any)?.entities || []
    );
  }

  get project() {
    if (!this.projectId) return null;
    const projects =
      ((this.rootStore as any).manholeProjectsStore as any)?.entities || [];
    return (
      projects.find(
        (p: any) => p.id === this.projectId || p.projectId === this.projectId
      ) || null
    );
  }

  get structures() {
    if (!this.projectId) return [];
    const allStructures =
      ((this.rootStore as any).manholeStructuresStore as any)?.entities || [];
    return allStructures
      .filter((s: any) => s.projectId === this.projectId)
      .sort((a: any, b: any) => {
        if (
          a.sequenceOrder !== undefined &&
          b.sequenceOrder !== undefined
        ) {
          return a.sequenceOrder - b.sequenceOrder;
        }
        return (
          new Date(a.createdAt || 0).getTime() -
          new Date(b.createdAt || 0).getTime()
        );
      });
  }

  get quote() {
    if (!this.projectId) return null;
    const allQuotes =
      ((this.rootStore as any).manholeQuotesStore as any)?.entities || [];
    return (
      allQuotes.find(
        (q: any) =>
          q.manholeProjectId === this.projectId ||
          q.projectId === this.projectId
      ) || null
    );
  }

  get quoteItems() {
    const quoteId = this.quote?.id;
    if (!quoteId) return [];
    const allItems =
      ((this.rootStore as any).manholeQuoteItemsStore as any)?.entities || [];
    return allItems
      .filter((item: any) => item.quoteId === quoteId)
      .sort((a: any, b: any) => (a.lineNumber || 0) - (b.lineNumber || 0));
  }

  get structureCodes() {
    return (
      ((this.rootStore as any).structureCodesStore as any)?.entities || []
    );
  }

  get customers() {
    return (
      ((this.rootStore as any).customersStore as any)?.entities || []
    );
  }

  // --- Per-structure data ---

  getStructureComponents(structureId: string) {
    const allSequences =
      ((this.rootStore as any).manholeSequencesStore as any)?.entities || [];
    return allSequences
      .filter((s: any) => s.structureId === structureId)
      .sort(
        (a: any, b: any) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0)
      );
  }

  getStructurePipes(structureId: string) {
    const allPipes =
      ((this.rootStore as any).manholePipeConnectionsStore as any)?.entities ||
      [];
    return allPipes
      .filter((p: any) => p.structureId === structureId)
      .sort((a: any, b: any) => (a.pipeNumber || 0) - (b.pipeNumber || 0));
  }

  getStructureQuoteItems(structureId: string) {
    return this.quoteItems.filter(
      (item: any) => item.structureId === structureId
    );
  }

  getStructureCodeName(structureCodeId: string) {
    if (!structureCodeId) return null;
    const code = this.structureCodes.find(
      (c: any) => c.id === structureCodeId
    );
    return code?.name || code?.code || null;
  }

  getStructurePrice(structureId: string) {
    const items = this.getStructureQuoteItems(structureId);
    return items.reduce(
      (sum: number, item: any) =>
        sum + (item.extendedCost || (item.unitCost || 0) * (item.quantity || 1)),
      0
    );
  }

  getStructurePieceCount(structureId: string) {
    const components = this.getStructureComponents(structureId);
    return components.reduce(
      (sum: number, c: any) => sum + (c.quantity || 1),
      0
    );
  }

  getStructureWeight(structureId: string) {
    const components = this.getStructureComponents(structureId);
    return components.reduce(
      (sum: number, c: any) => sum + (c.weight || 0),
      0
    );
  }

  getEffectiveStructureStatus(structure: any): string {
    // If the structure has an explicit status beyond "draft", respect it
    if (structure.status && structure.status !== "draft") {
      return structure.status;
    }

    // If the project has a more advanced status (quoted, awarded, completed),
    // structures inherit it — the backend doesn't update structure-level status
    // when a quote is generated, so we derive it here
    const projectStatus = this.project?.status;
    if (projectStatus && projectStatus !== "draft") {
      return projectStatus;
    }

    return structure.status || "draft";
  }

  getRimToInvert(structure: any) {
    if (
      structure.rimElevation != null &&
      structure.lowestInvert != null
    ) {
      return structure.rimElevation - structure.lowestInvert;
    }
    return null;
  }

  // --- Job-level totals ---

  get jobTotals() {
    const quote = this.quote;
    if (quote) {
      return {
        weight: quote.totalWeight || 0,
        pieces: quote.totalComponents || 0,
        subtotal: quote.subtotal || 0,
        tax: quote.tax || 0,
        total: quote.total || 0,
      };
    }

    // Fallback: compute from structures if no quote exists
    let weight = 0;
    let pieces = 0;
    let subtotal = 0;

    this.structures.forEach((s: any) => {
      weight += this.getStructureWeight(s.id);
      pieces += this.getStructurePieceCount(s.id);
      subtotal += this.getStructurePrice(s.id);
    });

    return {
      weight,
      pieces,
      subtotal,
      tax: 0,
      total: subtotal,
    };
  }

  reset() {
    this.projectId = null;
  }
}
